import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { getQuote } from "@/lib/api";
import GoldShieldLogo from "@/components/GoldShieldLogo";
import { 
  CheckCircle2, 
  Mail, 
  Clock,
  MapPin,
  Package,
  ArrowRight
} from "lucide-react";

export default function QuoteSubmittedPage() {
  const { quoteId } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchQuote = async () => {
      try {
        const data = await getQuote(quoteId);
        setQuote(data);
      } catch (error) {
        console.error("Error fetching quote:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchQuote();
  }, [quoteId]);

  const buildAddressLines = (address, fallbackPostcode) => {
    if (!address && !fallbackPostcode) {
      return [];
    }
    const lines = [];
    if (address?.company_name) {
      lines.push(address.company_name);
    }
    const contactLine = [address?.contact_name, address?.contact_phone].filter(Boolean).join(" — ");
    if (contactLine) {
      lines.push(contactLine);
    }
    if (address?.address_line_1) {
      lines.push(address.address_line_1);
    }
    if (address?.address_line_2) {
      lines.push(address.address_line_2);
    }
    const cityLine = [address?.city, address?.county].filter(Boolean).join(", ");
    if (cityLine) {
      lines.push(cityLine);
    }
    if (address?.postcode) {
      lines.push(address.postcode);
    }
    if (address?.country) {
      lines.push(address.country);
    }
    if (!lines.length && fallbackPostcode) {
      lines.push(fallbackPostcode);
    }
    return lines;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="animate-pulse text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#020617]">
      {/* Header */}
      <header className="bg-[#0f172a] border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <GoldShieldLogo className="w-10 h-12" />
            <div className="flex flex-col">
              <span className="font-['Barlow_Condensed'] text-xl font-bold text-white leading-tight">
                RTS EXPRESS
              </span>
              <span className="text-[10px] text-slate-400 tracking-wide">SAME DAY COURIER</span>
            </div>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-2xl mx-auto px-6 py-16">
        {/* Success Icon */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-[#22c55e]/10 rounded-full mb-6 animate-pulse-glow">
            <CheckCircle2 className="w-10 h-10 text-[#22c55e]" />
          </div>
          <h1 className="font-['Barlow_Condensed'] text-4xl font-bold text-white mb-2">
            QUOTE REQUEST <span className="text-[#22c55e]">SUBMITTED</span>
          </h1>
          <p className="text-slate-400">
            Thank you! We've received your request and will send you a quote shortly.
          </p>
        </div>

        {/* Reference */}
        <div className="bg-[#0f172a] border border-slate-800 rounded-sm p-6 mb-6">
          <div className="text-center mb-4">
            <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">Reference Number</p>
            <p className="font-mono text-lg text-white bg-slate-900 px-4 py-2 rounded inline-block" data-testid="quote-reference">
              {quoteId}
            </p>
          </div>
          <p className="text-slate-400 text-sm text-center">
            Save this reference number for your records.
          </p>
        </div>

        {/* What's Next */}
        <div className="bg-[#0f172a] border border-slate-800 rounded-sm p-6 mb-6">
          <h2 className="font-['Barlow_Condensed'] text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-[#22c55e]" />
            What Happens Next?
          </h2>
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-[#22c55e]/10 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-[#22c55e] text-sm font-bold">1</span>
              </div>
              <div>
                <p className="text-white font-medium">Email Confirmation Sent</p>
                <p className="text-slate-400 text-sm">Check your inbox at <strong>{quote?.email}</strong></p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-[#3b82f6]/10 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-[#3b82f6] text-sm font-bold">2</span>
              </div>
              <div>
                <p className="text-white font-medium">Quote Review</p>
                <p className="text-slate-400 text-sm">Our team will review your request and calculate the price</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-[#f59e0b]/10 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-[#f59e0b] text-sm font-bold">3</span>
              </div>
              <div>
                <p className="text-white font-medium">Quote Sent via Email</p>
                <p className="text-slate-400 text-sm">You'll receive a link to view and pay for your quote</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-8 h-8 bg-slate-700/50 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-slate-400 text-sm font-bold">4</span>
              </div>
              <div>
                <p className="text-white font-medium">Driver Dispatched</p>
                <p className="text-slate-400 text-sm">After payment confirmation, our driver will be on the way</p>
              </div>
            </div>
          </div>
        </div>

        {/* Summary */}
        {quote && (
          <div className="bg-[#0f172a] border border-slate-800 rounded-sm p-6 mb-6">
            <h2 className="font-['Barlow_Condensed'] text-xl font-bold text-white mb-4">
              Request Summary
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="flex items-start gap-2 text-slate-400" data-testid="quote-submitted-pickup-address">
                <MapPin className="w-4 h-4 text-[#22c55e] mt-1" />
                <div>
                  <span className="text-white font-semibold">Pickup Address</span>
                  <div className="mt-1 space-y-1">
                    {buildAddressLines(quote.pickup_address, quote.pickup_postcode).map((line, index) => (
                      <div key={`pickup-${index}`} className="text-slate-300">{line}</div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-2 text-slate-400" data-testid="quote-submitted-delivery-address">
                <MapPin className="w-4 h-4 text-[#3b82f6] mt-1" />
                <div>
                  <span className="text-white font-semibold">Delivery Address</span>
                  <div className="mt-1 space-y-1">
                    {buildAddressLines(quote.delivery_address, quote.delivery_postcode).map((line, index) => (
                      <div key={`delivery-${index}`} className="text-slate-300">{line}</div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 text-slate-400">
                <Package className="w-4 h-4 text-[#f59e0b]" />
                <span>Cargo: <span className="text-white capitalize">{quote.cargo_type}</span></span>
              </div>
              <div className="flex items-center gap-2 text-slate-400">
                <Clock className="w-4 h-4 text-slate-500" />
                <span>Urgency: <span className="text-white capitalize">{quote.urgency.replace('_', ' ')}</span></span>
              </div>
            </div>
          </div>
        )}

        {/* Important Notice */}
        <div className="bg-[#f59e0b]/10 border border-[#f59e0b]/20 rounded-sm p-4 mb-8">
          <p className="text-[#fbbf24] text-sm flex items-start gap-2">
            <Mail className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>
              <strong>Important:</strong> Payment is required before collection. 
              Once you receive the quote, you can pay securely via our payment portal. 
              No payment = No dispatch.
            </span>
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/">
            <Button 
              variant="outline" 
              className="w-full sm:w-auto border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-white"
            >
              Back to Home
            </Button>
          </Link>
          <Link to="/get-quote">
            <Button className="w-full sm:w-auto bg-[#22c55e] text-[#020617] hover:bg-[#22c55e]/90 font-semibold">
              Submit Another Request
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </main>
    </div>
  );
}
